import React from 'react'
import { EngagementTribeOrg } from '../../types/Engagement'
import { Member } from '../../types/Tribe'
import ProjectDateRange from './TribeProjectComponents/ProjectDateRange'
import ProjectName from './TribeProjectComponents/ProjectNameContainer'
import { AllGuildTribeHeader, AllGuildTypes } from '../../types/Guilds'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

interface Props {
  engagement: EngagementTribeOrg
  team:
    | {
        [AllGuildTypes.Developer]: Array<Member>
        [AllGuildTypes.QA]: Array<Member>
        [AllGuildTypes.Design]: Array<Member>
        [AllGuildTypes.DevOps]: Array<Member>
      }
    | undefined
  guildsInfo: Array<{ name: string; color: string; guildId: number }>
  maxPerDept: Record<string, number>
  teamName: string
  readOnly?: boolean
}

const DEPT_CONFIG = [
  {
    guildType: AllGuildTypes.Developer,
    label: AllGuildTribeHeader.Developer,
    cssClass: 'development',
  },
  {
    guildType: AllGuildTypes.QA,
    label: AllGuildTribeHeader.QA,
    cssClass: 'qa',
  },
  {
    guildType: AllGuildTypes.Design,
    label: AllGuildTribeHeader.Design,
    cssClass: 'design',
  },
  {
    guildType: AllGuildTypes.DevOps,
    label: AllGuildTribeHeader.DevOps,
    cssClass: 'ops',
  },
] as const

const TribeProjectContainer = ({
  engagement,
  team,
  maxPerDept,
  teamName,
  readOnly = false,
}: Props) => {
  const navigate = useNavigate()

  const handleMemberClick = (event: React.MouseEvent, memberId: number) => {
    event.stopPropagation()
    if (readOnly) return
    if (memberId !== undefined && memberId !== -1) {
      navigate(`/members/${memberId}`)
    }
  }

  const handleEngagementClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (readOnly) return
    if (engagement.id !== undefined && engagement.id !== -1) {
      navigate(`/engagements/${engagement.id}`)
    }
  }

  const activeDepts = DEPT_CONFIG.filter(
    dept => (maxPerDept[dept.guildType] ?? 0) > 0,
  )

  const formatSowDate = (date: Date | string) => {
    if (date === 'N/A') return 'N/A'
    return moment(date).format('MM/DD/YYYY')
  }

  return (
    <>
      <div className="tribe-org__engagement-card">
        <ProjectName
          projectName={engagement.projectName}
          clientName={engagement?.client?.name || ''}
          teamName={teamName}
          readOnly={readOnly}
          handleEngagementClick={handleEngagementClick}
        />
        <ProjectDateRange
          startDate={
            engagement.startDate !== 'N/A'
              ? new Date(engagement.startDate)
              : engagement.startDate
          }
          endDate={
            engagement.endDate !== 'N/A'
              ? new Date(engagement.endDate)
              : engagement.endDate
          }
          formatDate={formatSowDate}
        />
      </div>

      <div className="tribe-org__member-list">
        {!team ? (
          <div className="tribe-org__empty-state">
            <div className="tribe-org__empty-state-label">Open</div>
            <div className="tribe-org__empty-state-text">
              No team members assigned
            </div>
          </div>
        ) : (
          activeDepts.map(dept => {
            const members = team[dept.guildType] ?? []
            const padCount = Math.max(
              0,
              (maxPerDept[dept.guildType] ?? 0) - members.length,
            )

            return (
              <React.Fragment key={dept.guildType}>
                <div className="tribe-org__dept-header">
                  <span
                    className={`tribe-org__dept-label tribe-org__dept-label--${dept.cssClass}`}
                  >
                    {dept.label}
                  </span>
                  <span
                    className={`tribe-org__dept-line tribe-org__dept-line--${dept.cssClass}`}
                  />
                </div>
                {members.map((member, index) => {
                  const name = `${member.firstName} ${member.lastName}`.trim()
                  const isLead = !!member.role

                  return (
                    <div
                      key={`${dept.guildType}-${index}`}
                      className={`tribe-org__member-row tribe-org__member-row--${
                        dept.cssClass
                      }${
                        !readOnly && member.id !== -1
                          ? ' tribe-org__member-row--clickable'
                          : ''
                      }`}
                      onClick={
                        !readOnly && member.id !== -1
                          ? e => handleMemberClick(e, member.id)
                          : undefined
                      }
                    >
                      <span>{name}</span>
                      {isLead && (
                        <span className="tribe-org__lead-badge">
                          {member.role}
                        </span>
                      )}
                    </div>
                  )
                })}
                {Array.from({ length: padCount }, (_, index) => (
                  <div
                    key={`pad-${dept.guildType}-${index}`}
                    className={`tribe-org__member-pad tribe-org__member-pad--${dept.cssClass}`}
                  />
                ))}
              </React.Fragment>
            )
          })
        )}
      </div>
    </>
  )
}

export default TribeProjectContainer
